import React from 'react';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import { formatDate } from '../../utils/formatters';

interface CalendarListProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}

export default function CalendarList({ events, onEventClick }: CalendarListProps) {
  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const groupedEvents = sortedEvents.reduce((groups, event) => {
    const date = new Date(event.startTime).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {} as Record<string, CalendarEvent[]>);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Upcoming Events</h3>
      </div>

      <div className="divide-y divide-gray-200">
        {Object.entries(groupedEvents).map(([date, dayEvents]) => (
          <div key={date} className="p-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              {formatDate(date)}
            </h4>
            <div className="space-y-4">
              {dayEvents.map(event => (
                <button
                  key={event.id}
                  onClick={() => onEventClick(event)}
                  className="w-full flex items-start p-4 hover:bg-gray-50 rounded-lg"
                >
                  <div className={`flex-shrink-0 w-4 h-4 rounded-full mt-1 ${
                    event.priority === 'high'
                      ? 'bg-red-400'
                      : event.priority === 'medium'
                      ? 'bg-yellow-400'
                      : 'bg-green-400'
                  }`} />
                  <div className="ml-4 flex-1">
                    <h5 className="text-sm font-medium text-gray-900">{event.title}</h5>
                    <p className="mt-1 text-sm text-gray-500">{event.description}</p>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {new Date(event.startTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {event.dealId && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Deal #{event.dealId}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}